import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X, Loader2, MessageSquare, Clock } from 'lucide-react';
import { ConversationSearchResult } from '../../../../services/legalAIService';

interface ConversationSearchProps {
  onSearch: (query: string, contractId?: string) => Promise<void>;
  onClearSearch: () => void;
  onSelectConversation: (conversationId: string) => Promise<void>;
  searchResults: ConversationSearchResult[];
  isSearching: boolean;
  searchQuery: string;
  contractId?: string;
}

export const ConversationSearch: React.FC<ConversationSearchProps> = ({
  onSearch,
  onClearSearch,
  onSelectConversation,
  searchResults,
  isSearching,
  searchQuery,
  contractId
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length >= 2) {
      debounceRef.current = setTimeout(() => {
        onSearch(value, contractId);
      }, 300);
    } else {
      onClearSearch();
    }
  }, [onSearch, onClearSearch, contractId]);

  // Clear on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when there are results
  useEffect(() => {
    if (searchResults.length > 0 || (searchQuery.length >= 2 && !isSearching)) {
      setIsOpen(true);
    }
  }, [searchResults, searchQuery, isSearching]);

  const handleClear = () => {
    setInputValue('');
    onClearSearch();
    setIsOpen(false);
  };

  const handleSelectResult = async (conversationId: string) => {
    await onSelectConversation(conversationId);
    handleClear();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-900 text-inherit rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setIsOpen(true)}
          placeholder="Search conversations..."
          className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {(inputValue || isSearching) && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {isSearching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <X className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {searchQuery.length >= 2 ? (
                isSearching ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </div>
                ) : (
                  <span>No conversations found for "{searchQuery}"</span>
                )
              ) : (
                <span>Type at least 2 characters to search</span>
              )}
            </div>
          ) : (
            <div className="py-2">
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              </div>
              {searchResults.map((result) => (
                <button
                  key={result._id}
                  onClick={() => handleSelectResult(result._id)}
                  className="w-full px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors border-t border-gray-100 dark:border-gray-800 first:border-t-0"
                >
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {result.titleMatch
                            ? highlightMatch(result.title, searchQuery)
                            : result.title}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatDate(result.updatedAt)}
                        </span>
                      </div>
                      {result.matchingMessages.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {result.matchingMessages.slice(0, 2).map((msg, idx) => (
                            <p
                              key={idx}
                              className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2"
                            >
                              <span className="font-medium text-gray-600 dark:text-gray-300">
                                {msg.role === 'user' ? 'You: ' : 'AI: '}
                              </span>
                              {highlightMatch(msg.preview, searchQuery)}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                        <span>{result.messageCount} messages</span>
                        <span>•</span>
                        <span className="capitalize">{result.aiMode.replace(/-/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationSearch;
